/**
 * This TypeScript script is designed to work with a specific HTML markup for indexing and searching documents using lunr.js.
 * The expected HTML structure should include the following elements:
 *
 * 1. Indexable Elements:
 *    - Elements that contain data to be indexed should have a 'data-obj' attribute and a 'data-obj-data' attribute.
 *    - The 'data-obj' attribute indicates the type of object.
 *    - The 'data-obj-data' attribute contains the JSON data of the object to be indexed.
 *    - Example: <div data-obj="product" data-obj-data='{"id":1, "title":"Example", "category":"technology"}'></div>
 *
 * 2. Searchable Elements:
 *    - Elements used for searching should have a 'data-search-obj' attribute.
 *    - This attribute specifies the type of objects to search for (should match with 'data-obj' types).
 *    - Inside these elements, input fields with 'data-search-field' attributes are used to capture search criteria.
 *    - The 'data-search-field' attribute specifies the field name in the index to be searched.
 *    - For the 'category' field, a select form field can be used.
 *    Example:
 *      <div data-search-obj="article">
 *        <input data-search-field="title" type="text">
 *        <select data-search-field="category">
 *          <option value="Technology">Technology</option>
 *          <option value="Science">Science</option>
 *        </select>
 *        <button type="submit">Search</button>
 *      </div>
 *
 * This setup allows for dynamic indexing and searching of document data embedded in HTML using lunr.js.
 */

import * as $ from "jquery";
import Debouncer from '../../lib/Debouncer';
// import lunr correctly from node_modules
import * as lunr from 'lunr';
import replaceStemerWithOneThatKeepsOriginalAndStems from './lunr/replaceStemerWithOneThatKeepsOriginalAndStems';

// Documents to be indexed
const documents: any[] = [];
const $objs = $('[data-obj][data-obj-data]');
$objs.each((index, element) => {
    const json = element.getAttribute('data-obj-data')!;
    const data = JSON.parse(json);
    data.objType = element.getAttribute('data-obj');
    data.domElement = element;
    documents.push(data);
});

// Create searchable index of documents
const idx = lunr(function () {
    this.use(replaceStemerWithOneThatKeepsOriginalAndStems);
    this.ref('id');
    documents.forEach(doc => {
        Object.keys(doc).forEach(propName => {
            if (!(this._fields as any)[propName]) {
                this.field(propName);
            }
        });
        this.add(doc);
    });
});

// Search index
const $searches = $('[data-search-obj]');

// Function to toggle 'search-active' class
const toggleSearchActiveClass = (isActive: boolean) => {
    const elementToToggle = document.querySelector('.kioskLandingPage'); // Change to your target element
    if (elementToToggle) {
        if (isActive) {
            elementToToggle.classList.add('search-active');
        } else {
            elementToToggle.classList.remove('search-active');
        }
    }
};

const updateResults = (event: JQuery.TriggeredEvent) => {
    const $searchContainer = $(event.target).closest('[data-search-obj]');
    updateSearchResults($searchContainer);
    event.preventDefault();
};

$searches.on('change submit', updateResults);
$searches.on('keyup', new Debouncer(updateResults, false, 1000).run);
$searches.each((index, element) => updateSearchResults($(element)));

function updateSearchResults($searchContainer: JQuery) {
    let query = '';
    const objType = $searchContainer.attr('data-search-obj');
    query += '+objType:' + objType;
    const $fields = $searchContainer.find('[data-search-field]');
    $fields.each((index, element) => {
        const input = element as HTMLInputElement;
        const field = input.getAttribute('data-search-field')!;
        const type = input.getAttribute('type') ?? 'text';
        let value = type === 'radio' || type === 'checkbox' ? input.checked ? input.value : '' : input.value;
        value = value.replace(/[^\sA-Za-z_0-9]+/g, '');
        if (value) {
            value.split(/\s+/).forEach(val => {
                if (val) {
                    query += ` +${field}:${val}*`;
                }
            });
        }
    });
    const matches: lunr.Index.Result[] = idx.search(query);
    const matchIds = matches.map(result => result.ref);

    // check if any input in search field has value
    if($fields.val() !== '') {
    	document.querySelector('.kioskLandingPage').classList.add('query-active');
    } else {
    	document.querySelector('.kioskLandingPage').classList.remove('query-active');
    }

    // if no matches show 'no results' message
	if(!matches.length) {
		document.querySelector('.kioskLandingPage__results--noResults').classList.add('show');
	} else {
		document.querySelector('.kioskLandingPage__results--noResults').classList.remove('show');
	}

    documents.forEach(doc => {
        if (doc.objType === objType) {
            doc.domElement.setAttribute('search-match', matchIds.includes(doc.id) ? 'true' : 'false');
        }
    });
}
